import * as React from "react";
import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Database, AlertTriangle, Search } from "lucide-react";
import { ChatBlockedState } from "../../../../chat/components/molecules/ChatBlockedState";
import {
  TaskpanePanelShell,
  PanelEmptyState,
  PanelLoadingState,
} from "@/taskpane/shared/components/panel-layout";
import { ProjectVariable } from "../../../../../types/navigationTypes";
import { 
  getProjectVariablesByExecution,
  upsertProjectVariable,
  deleteProjectVariable,
  scanAndSaveDocumentVariables,
  replaceVariableInDocument,
  replaceAllVariablesInDocument
} from "../../../services/projectVariablesService";
import { WordService } from "../../../../../shared/services/wordService";
import { 
  ProjectVariablesHeader,
  VariableItem,
  CreateVariableModal
} from "../../molecules";

interface ProjectVariablesPanelProps {
  executionId?: string;
  onOpenProjectSelector?: () => void;
}

const ProjectVariablesPanel: React.FC<ProjectVariablesPanelProps> = ({
  executionId,
  onOpenProjectSelector,
}) => {
  const [variables, setVariables] = useState<ProjectVariable[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [isReplacing, setIsReplacing] = useState(false);
  const [replacingVariableId, setReplacingVariableId] = useState<string | null>(null);

  const filterVariables = (
    variableList: ProjectVariable[],
    searchTerm: string
  ): ProjectVariable[] => {
    if (!searchTerm.trim()) {
      return variableList;
    }
    
    const lowerSearch = searchTerm.toLowerCase();
    
    return variableList.filter(variable => 
      variable.name.toLowerCase().includes(lowerSearch) ||
      (variable.value && variable.value.toLowerCase().includes(lowerSearch))
    );
  };

  const loadVariables = useCallback(async () => {
    if (!executionId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const data = await getProjectVariablesByExecution(executionId);
      setVariables(data);
    } catch (err) {
      console.error("Error loading project variables:", err);
      setError("Error loading project variables");
    } finally {
      setLoading(false);
    }
  }, [executionId]);

  // Función para actualizar variables sin mostrar loading (evita centelleo)
  const refreshVariables = useCallback(async () => {
    if (!executionId) return;
    
    try {
      const data = await getProjectVariablesByExecution(executionId);
      setVariables(data);
    } catch (err) {
      console.error("Error refreshing variables:", err);
      // No mostrar error al usuario si ya hay datos cargados
    }
  }, [executionId]);

  const scanDocumentForVariables = useCallback(async () => {
    if (!executionId) return;
    
    try {
      setIsScanning(true);
      console.log('🔍 Scanning document for variables...');
      
      // Obtener el texto completo del documento
      const documentText = await WordService.getFullDocumentText();
      
      // Buscar y guardar variables
      const result = await scanAndSaveDocumentVariables(executionId, documentText);
      
      console.log(`✅ Scan completed: ${result.found} found, ${result.saved} saved`);
      
      // Recargar las variables después del escaneo (sin mostrar loading)
      await refreshVariables();
    } catch (err) {
      console.error("Error scanning document:", err);
      // No mostrar error al usuario, solo loguear
      // El escaneo es una operación en segundo plano
    } finally {
      setIsScanning(false);
    }
  }, [executionId, refreshVariables]);

  useEffect(() => {
    if (executionId) {
      loadVariables();
      // Buscar variables automáticamente al cargar
      scanDocumentForVariables();
    } else {
      setLoading(false);
      setError(null);
    }
  }, [executionId, loadVariables, scanDocumentForVariables]);

  const handleCreateVariable = async (name: string, value: string) => {
    try {
      await upsertProjectVariable(executionId!, name, value);
      await refreshVariables();
    } catch (err) {
      console.error("Error creating variable:", err);
      throw err;
    }
  };

  const handleEditVariable = async (variable: ProjectVariable, newValue: string) => {
    try {
      await upsertProjectVariable(executionId!, variable.name, newValue);
      await refreshVariables();
    } catch (err) {
      console.error("Error updating variable:", err);
      alert("Error updating variable");
    }
  };

  const handleDeleteVariable = async (variableId: string) => {
    try {
      await deleteProjectVariable(variableId);
      await refreshVariables();
    } catch (err) {
      console.error("Error deleting variable:", err);
      alert("Error deleting variable");
    }
  };

  const handleReplaceAll = async () => {
    const variablesWithValue = variables.filter(v => v.value && v.value.trim() !== '');
    
    if (variablesWithValue.length === 0) {
      alert('No variables with values to replace');
      return;
    }

    if (!confirm(`Replace all ${variablesWithValue.length} variables with values in the document?`)) {
      return;
    }

    try {
      setIsReplacing(true);
      const result = await replaceAllVariablesInDocument(variables);
      
      alert(
        `Replacement completed:\n` +
        `- ${result.replacedVariables} variables replaced\n` +
        `- ${result.totalOccurrences} total occurrences`
      );
    } catch (err) {
      console.error("Error replacing variables:", err);
      alert("Error replacing variables in the document");
    } finally {
      setIsReplacing(false);
    }
  };

  const handleReplaceVariable = async (variable: ProjectVariable) => {
    if (!variable.value || variable.value.trim() === '') {
      alert(`Variable "${variable.name}" has no value assigned`);
      return;
    }

    try {
      setReplacingVariableId(variable.id);
      const count = await replaceVariableInDocument(variable.name, variable.value);
      
      if (count > 0) {
        alert(`Replaced ${count} occurrences of {{${variable.name}}}`);
      } else {
        alert(`No occurrences of {{${variable.name}}} found in the document`);
      }
    } catch (err) {
      console.error("Error replacing variable:", err);
      alert("Error replacing variable in the document");
    } finally {
      setReplacingVariableId(null);
    }
  };

  if (!executionId) {
    if (onOpenProjectSelector) {
      return (
        <TaskpanePanelShell variant="flex" className="items-center justify-center !p-4">
          <ChatBlockedState onOpenSelector={onOpenProjectSelector} />
        </TaskpanePanelShell>
      );
    }
    return (
      <TaskpanePanelShell>
        <PanelEmptyState
          icon={Database}
          title="No execution selected"
          description="Select a project execution to view and edit variables."
          compact
        />
      </TaskpanePanelShell>
    );
  }

  if (loading) {
    return (
      <TaskpanePanelShell>
        <PanelLoadingState message="Loading variables…" />
      </TaskpanePanelShell>
    );
  }

  if (error) {
    return (
      <TaskpanePanelShell>
        <div className="flex flex-col items-center justify-center gap-3 p-12">
          <AlertTriangle className="h-8 w-8 text-red-500" />
          <p className="m-0 text-center text-xs text-slate-600">{error}</p>
          <Button onClick={() => loadVariables()} className="mt-2" size="sm" variant="outline">
            Retry
          </Button>
        </div>
      </TaskpanePanelShell>
    );
  }

  const filteredVariables = filterVariables(variables, searchTerm);
  const variablesWithValue = variables.filter(v => v.value && v.value.trim() !== '').length;

  return (
    <TaskpanePanelShell>
      <ProjectVariablesHeader
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        variables={variables}
        onCreateVariable={() => setShowCreateModal(true)}
        onScanDocument={scanDocumentForVariables}
        isScanning={isScanning}
        onReplaceAll={handleReplaceAll}
        isReplacing={isReplacing}
        variablesWithValue={variablesWithValue}
      />

      {filteredVariables.length === 0 && variables.length === 0 ? (
        <PanelEmptyState
          icon={Database}
          title="No project variables"
          description="Store key data for this execution. Scan the document or create a variable manually."
        >
          <Button onClick={() => setShowCreateModal(true)} size="sm">
            Create first variable
          </Button>
        </PanelEmptyState>
      ) : filteredVariables.length === 0 ? (
        <PanelEmptyState
          icon={Search}
          title="No variables match your search"
          description="Try another term or clear the search field."
          compact
        />
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-sm">
          <div className="grid grid-cols-[200px_1fr_120px_140px] gap-3 border-b border-slate-100 bg-slate-50/80 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            <div>Name</div>
            <div>Value</div>
            <div>Updated</div>
            <div className="text-right">Actions</div>
          </div>

          {filteredVariables.map((variable) => (
            <VariableItem
              key={variable.id}
              variable={variable}
              onEdit={handleEditVariable}
              onDelete={handleDeleteVariable}
              onReplace={handleReplaceVariable}
              isReplacing={replacingVariableId === variable.id}
            />
          ))}
        </div>
      )}

      <CreateVariableModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreateVariable={handleCreateVariable}
      />
    </TaskpanePanelShell>
  );
};

export default ProjectVariablesPanel;

